import { useCallback } from 'react';
import type { Getter } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import useAnalytics from '../../../../analytics/useAnalytics';
import { useMetrics } from '../../../../metrics/useMetrics';
import { useMediaStatePlaySpeed } from '../../../labelling/providers/hooks/use-media-state';
import type { AtomBucketTagsT, AtomDetailTagsT, CaptureGaugeInputT, CaptureLatencyInputT, IncrementCountInputT, MediaDerivedTagsT, PerformanceEventT, PerformanceMetricActionT, SessionDerivedTagsT } from '../../analytics/types/performance';
import { numFramesAtom } from '../../atoms/annotation-atoms';
import { complexityBucketAtom, framePointCountAtom, framePointCountBucketAtom, totalPointCountAtom, totalPointCountBucketAtom } from '../../atoms/performance-atoms';
import { sessionStartTimeAtom } from '../../atoms/session-atoms';
import { selectedToolAtom } from '../../atoms/tool-atoms';
import { FRAME_COUNT_THRESHOLDS, SESSION_DURATION_THRESHOLDS } from '../../constants/performance';
import { bucketize } from '../../utils/bucketize';
import { getHeapTags } from '../../utils/performance-tag-helpers';
type MetricType = 'latency' | 'gauge' | 'count';
const IS_DEV = typeof __DEV__ !== 'undefined' && __DEV__;
function logMetricDev(type: 'latency' | 'gauge' | 'count', name: string, tags: object, value?: number) {
  if (!IS_DEV) return;
  let label = `🚀 ~ [perf] ${name}`;
  switch (type) {
    case 'latency':
      {
        label += ` ${value?.toFixed(1)}ms`;
        break;
      }
    case 'gauge':
      {
        label += ` = ${value}`;
        break;
      }
    case 'count':
      {
        label += ' +1';
        break;
      }
  }
  // eslint-disable-next-line no-console
  console.groupCollapsed(label);
  // eslint-disable-next-line no-console
  console.table(tags);
  // eslint-disable-next-line no-console
  console.groupEnd();
}
function getAtomBucketTags(get: Getter): Partial<AtomBucketTagsT> {
  try {
    return {
      complexityBucket: get(complexityBucketAtom),
      totalPointCountBucket: get(totalPointCountBucketAtom),
      framePointCountBucket: get(framePointCountBucketAtom),
      toolType: get(selectedToolAtom),
      frameCountBucket: bucketize(get(numFramesAtom), [...FRAME_COUNT_THRESHOLDS])
    };
  } catch {
    return {};
  }
}
function getAtomDetailTags(get: Getter): Partial<AtomDetailTagsT> {
  try {
    return {
      ...getAtomBucketTags(get),
      totalPointCount: get(totalPointCountAtom),
      framePointCount: get(framePointCountAtom),
      frameCount: get(numFramesAtom)
    };
  } catch {
    return {};
  }
}
function getMediaDerivedTags(playSpeed: number): MediaDerivedTagsT {
  return {
    playSpeed
  };
}
function getSessionDerivedTags(get: Getter): SessionDerivedTagsT {
  const sessionStartTime = get(sessionStartTimeAtom);
  const sessionMinutes = (Date.now() - sessionStartTime) / 60000;
  return {
    sessionDurationBucket: bucketize(sessionMinutes, [...SESSION_DURATION_THRESHOLDS])
  };
}

/**
 * Unified performance metrics hook. Emits to M3 (bucketed tags) and Heatpipe (full detail).
 * All tags are auto-derived at emit time; callers only pass event-specific tags.
 */
export function usePerformanceMetrics() {
  const m3 = useMetrics();
  const {
    sendEvent
  } = useAnalytics();
  const playSpeed = useMediaStatePlaySpeed();
  const emitMetric = useAtomCallback(useCallback(
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  (get, _set, type: MetricType, name: PerformanceMetricActionT, value: number, tags?: object) => {
    const bucketTags = getAtomBucketTags(get);
    const detailTags = getAtomDetailTags(get);
    const heapTags = getHeapTags();
    const mediaTags = getMediaDerivedTags(playSpeed);
    const sessionTags = getSessionDerivedTags(get);
    const m3Tags = {
      ...bucketTags,
      ...heapTags,
      ...mediaTags,
      ...sessionTags,
      ...tags
    };
    const analyticsPayload = {
      ...detailTags,
      ...heapTags,
      ...mediaTags,
      ...sessionTags,
      ...tags
    };
    if (type === 'count') {
      m3.incrementCount(name, m3Tags);
    } else {
      m3.captureHistogram(name, value, m3Tags);
    }
    const payload = type === 'latency' ? {
      durationMs: value,
      ...analyticsPayload
    } : type === 'gauge' ? {
      value,
      ...analyticsPayload
    } : analyticsPayload;
    sendEvent(({
      action: name,
      payload
    } as PerformanceEventT));
    logMetricDev(type, name, payload, type !== 'count' ? value : undefined);
  }, [playSpeed]));
  const captureLatency = useCallback((input: CaptureLatencyInputT) => {
    emitMetric('latency', input.name, input.durationMs, input.tags);
  }, [emitMetric]);
  const captureGauge = useCallback((input: CaptureGaugeInputT) => {
    emitMetric('gauge', input.name, input.value, input.tags);
  }, [emitMetric]);
  const incrementCount = useCallback((input: IncrementCountInputT) => {
    emitMetric('count', input.name, 0, input.tags);
  }, [emitMetric]);
  return {
    captureLatency,
    captureGauge,
    incrementCount
  };
}